import { useNavigate } from "@tanstack/react-router";
import { Button, Popconfirm, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ABSENCE_STATUS_LABELS, ABSENCE_TYPE_LABELS, type Absence } from "@/modules/absences/types";
import { DayDetailDrawer } from "@/modules/attendance/components/DayDetailDrawer";
import { ATTENDANCE_STATUS_LABELS, type AttendanceDay } from "@/modules/attendance/types";
import { useActivateDevice, useDeactivateDevice } from "@/modules/devices/hooks/useDevices";
import type { Device } from "@/modules/devices/types";
import { type Lead, SUSPICION_LABELS } from "@/modules/leads/types";
import type { ShiftInstance } from "@/modules/schedules/types";
import type { Task } from "@/modules/tasks/types";
import { PermissionGate } from "@/shared/components/PermissionGate";
import { EmployeeListTab } from "./EmployeeListTab";

/**
 * ⚠️ **Qo'shni modullardan TIP va YORLIQ lug'ati import qilinadi, HOOK
 * emas.** Hook (`useAttendanceDays`) o'z filtr holatini olib keladi va
 * profilga u kerak emas — bu yerda so'rov `EmployeeListTab` orqali
 * `employeeId` bilan quriladi. Lug'atlarni nusxalash esa ikki ro'yxatni
 * jimgina ajratardi: bir joyda «Uzrli», boshqasida `undefined`.
 */

/** ISO timestamptz → mahalliy "YYYY-MM-DD HH:mm". Bo'sh qiymat uchun «—». */
function vaqt(value: string | null): string {
	return value ? dayjs(value).format("YYYY-MM-DD HH:mm") : "—";
}

/** ISO timestamptz → mahalliy "HH:mm". Bo'sh qiymat uchun «—». */
function soat(value: string | null): string {
	return value ? dayjs(value).format("HH:mm") : "—";
}

/**
 * «Davomat» yorlig'i — qator KUN TAFSILOTIGA ochiladi.
 *
 * ⚠️ Tafsilot `DayDetailDrawer` bilan ko'rsatiladi (`AttendanceDaysPage`
 * dagi bilan AYNI komponent): unda hodisalar lentasi va tuzatish oynasi
 * bor, ya'ni ikkinchi nusxa yozish faqat farqlanish manbai bo'lardi.
 */
export function EmployeeAttendanceTab({ employeeId }: { employeeId: string }) {
	const { t } = useTranslation("employees");
	const [ochiqKun, setOchiqKun] = useState<string | null>(null);

	const columns: ColumnsType<AttendanceDay> = [
		{ title: t("attendanceTab.columns.date"), dataIndex: "workDate", key: "workDate" },
		{
			title: t("attendanceTab.columns.status"),
			key: "status",
			render: (_, row) => (
				<Tag color={ATTENDANCE_STATUS_LABELS[row.status].color}>
					{ATTENDANCE_STATUS_LABELS[row.status].label}
				</Tag>
			),
		},
		{
			title: t("attendanceTab.columns.checkIn"),
			key: "in",
			render: (_, row) => soat(row.firstCheckInAt),
		},
		{
			title: t("attendanceTab.columns.checkOut"),
			key: "out",
			render: (_, row) => soat(row.lastCheckOutAt),
		},
		{
			title: t("attendanceTab.columns.lateMinutes"),
			dataIndex: "lateMinutes",
			key: "lateMinutes",
		},
		{
			title: t("attendanceTab.columns.earlyMinutes"),
			dataIndex: "earlyMinutes",
			key: "earlyMinutes",
		},
		{
			title: t("attendanceTab.columns.timeInZone"),
			dataIndex: "timeInZoneMin",
			key: "timeInZoneMin",
		},
		{
			title: t("attendanceTab.columns.departures"),
			dataIndex: "departuresCount",
			key: "departuresCount",
		},
	];

	return (
		<>
			<EmployeeListTab<AttendanceDay>
				employeeId={employeeId}
				// ⚠️ Prefiks `"attendance-days"` — `useCorrectAttendanceDay`
				// aynan shu kalitni invalidatsiya qiladi. `["employees", …]`
				// bilan boshlansa, tuzatishdan keyin jadval eski holatda
				// qolardi.
				queryKey={["attendance-days", "employee"]}
				url="/attendance/days"
				columns={columns}
				onRowClick={(row) => setOchiqKun(row.id)}
			/>

			<DayDetailDrawer dayId={ochiqKun} onClose={() => setOchiqKun(null)} />
		</>
	);
}

/** «Smenalar» — reja; qator ichkariga kirmaydi (smena tafsiloti marshruti yo'q). */
export function EmployeeShiftsTab({ employeeId }: { employeeId: string }) {
	const { t } = useTranslation("employees");
	const columns: ColumnsType<ShiftInstance> = [
		{ title: t("shiftsTab.columns.workDate"), dataIndex: "workDate", key: "workDate" },
		{
			title: t("shiftsTab.columns.plannedStart"),
			dataIndex: "plannedStartAt",
			key: "plannedStartAt",
			render: (value: string) => vaqt(value),
		},
		{
			title: t("shiftsTab.columns.plannedEnd"),
			dataIndex: "plannedEndAt",
			key: "plannedEndAt",
			render: (value: string) => vaqt(value),
		},
	];

	return (
		<EmployeeListTab<ShiftInstance>
			employeeId={employeeId}
			queryKey={["employees", "tab", "shift-instances"]}
			url="/shift-instances"
			columns={columns}
		/>
	);
}

export function EmployeeAbsencesTab({ employeeId }: { employeeId: string }) {
	const { t } = useTranslation("employees");
	const columns: ColumnsType<Absence> = [
		{
			title: t("absencesTab.columns.type"),
			dataIndex: "type",
			key: "type",
			render: (value: Absence["type"]) => ABSENCE_TYPE_LABELS[value],
		},
		{
			title: t("absencesTab.columns.range"),
			key: "range",
			render: (_, row) => `${row.dateFrom} — ${row.dateTo}`,
		},
		{
			title: t("absencesTab.columns.status"),
			dataIndex: "status",
			key: "status",
			render: (value: Absence["status"]) => (
				<Tag color={ABSENCE_STATUS_LABELS[value].color}>{ABSENCE_STATUS_LABELS[value].label}</Tag>
			),
		},
	];

	return (
		<EmployeeListTab<Absence>
			employeeId={employeeId}
			queryKey={["employees", "tab", "absences"]}
			url="/absences"
			columns={columns}
		/>
	);
}

/** «Topshiriqlar» — qator `/tasks/$taskId` ga olib boradi. */
export function EmployeeTasksTab({ employeeId }: { employeeId: string }) {
	const { t } = useTranslation("employees");
	const navigate = useNavigate();

	const columns: ColumnsType<Task> = [
		{ title: t("tasksTab.columns.name"), dataIndex: "name", key: "name" },
		{
			title: t("tasksTab.columns.status"),
			dataIndex: "status",
			key: "status",
			// ⚠️ `tasks:status.*` — `modules/tasks` ning O'Z namespace'idan
			// (`TASK_STATUS_LABELS` P7 migratsiyasida shu kalitlarga
			// ko'chirilgan). Import qilinmaydi — modul chegarasi.
			render: (value: Task["status"]) => t(`tasks:status.${value}`),
		},
		{
			title: t("tasksTab.columns.dueTo"),
			dataIndex: "dueTo",
			key: "dueTo",
			render: (value: string) => vaqt(value),
		},
		{
			title: t("tasksTab.columns.completed"),
			dataIndex: "completedCount",
			key: "completedCount",
		},
	];

	return (
		<EmployeeListTab<Task>
			employeeId={employeeId}
			queryKey={["employees", "tab", "tasks"]}
			url="/tasks"
			columns={columns}
			onRowClick={(row) => navigate({ to: "/tasks/$taskId", params: { taskId: row.id } })}
		/>
	);
}

/** «Lidlar» — qator `/leads/$leadId` ga olib boradi. */
export function EmployeeLeadsTab({ employeeId }: { employeeId: string }) {
	const { t } = useTranslation("employees");
	const navigate = useNavigate();

	const columns: ColumnsType<Lead> = [
		{
			title: t("leadsTab.columns.date"),
			key: "occurredAt",
			render: (_, row) => vaqt(row.occurredAt),
		},
		{
			title: t("leadsTab.columns.form"),
			key: "form",
			render: (_, row) => `${row.formName} (v${row.formVersion})`,
		},
		{ title: t("leadsTab.columns.place"), dataIndex: "resolvedLabel", key: "resolvedLabel" },
		{ title: t("leadsTab.columns.task"), key: "task", render: (_, row) => row.taskName ?? "—" },
		{
			title: t("leadsTab.columns.flags"),
			key: "flags",
			render: (_, row) =>
				row.suspicionFlags.length === 0
					? "—"
					: row.suspicionFlags.map((flag) => (
							<Tag color="orange" key={flag}>
								{SUSPICION_LABELS[flag] ?? flag}
							</Tag>
						)),
		},
	];

	return (
		<EmployeeListTab<Lead>
			employeeId={employeeId}
			queryKey={["employees", "tab", "leads"]}
			url="/leads"
			columns={columns}
			onRowClick={(row) => navigate({ to: "/leads/$leadId", params: { leadId: row.id } })}
		/>
	);
}

/**
 * «Qurilmalar» — xodimga bog'langan telefonlar.
 *
 * ⚠️ Bekor qilingan qurilma ham ko'rinadi (`isActive` filtri
 * YUBORILMAYDI): «qurilma 2 marta almashtirilgan» degan xavf signali
 * aynan shu tarixdan o'qiladi. Sarlavha kartasidagi fakt ustuni esa
 * AKSINCHA — u faqat amaldagi bittasini so'raydi.
 *
 * ⚠️ Yoqish/o'chirish amali AYNAN SHU YERDA (P3 spec §6) — `DevicesPage`
 * endi faqat ma'lumotnoma, amal tugmalari yo'q. Faol qurilma uchun
 * «Deaktivatsiya qilish» (`devices:delete`, `useDeactivateDevice`),
 * nofaol uchun «Faollashtirish» (`devices:update`, `useActivateDevice`) —
 * ikkalasi ham `Popconfirm` bilan, boshqa mutatsiya tugmalari bilan bir
 * xil naqsh.
 */
export function EmployeeDevicesTab({ employeeId }: { employeeId: string }) {
	const { t } = useTranslation("employees");
	const deactivateDevice = useDeactivateDevice();
	const activateDevice = useActivateDevice();

	const columns: ColumnsType<Device> = [
		{ title: t("devicesTab.columns.model"), key: "model", render: (_, row) => row.model ?? "—" },
		{
			title: t("devicesTab.columns.os"),
			key: "os",
			render: (_, row) => [row.os, row.osVersion].filter(Boolean).join(" ") || "—",
		},
		{
			title: t("devicesTab.columns.appVersion"),
			key: "appVersion",
			render: (_, row) => row.appVersion ?? "—",
		},
		{
			title: t("devicesTab.columns.boundAt"),
			key: "boundAt",
			render: (_, row) => vaqt(row.boundAt),
		},
		{
			title: t("devicesTab.columns.lastSeenAt"),
			key: "lastSeenAt",
			render: (_, row) => vaqt(row.lastSeenAt),
		},
		{
			title: t("devicesTab.columns.status"),
			key: "isActive",
			render: (_, row) =>
				row.isActive ? (
					<Tag color="blue">{t("devicesTab.statusActive")}</Tag>
				) : (
					<Tag>{t("devicesTab.statusInactive")}</Tag>
				),
		},
		{
			title: t("devicesTab.columns.actions"),
			key: "actions",
			align: "right",
			render: (_, row) =>
				row.isActive ? (
					<PermissionGate resource="devices" action="delete">
						<Popconfirm
							title={t("devicesTab.deactivateConfirmTitle")}
							description={t("devicesTab.deactivateConfirmDescription")}
							okText={t("devicesTab.deactivateConfirmOk")}
							cancelText={t("devicesTab.confirmCancel")}
							okButtonProps={{ danger: true }}
							onConfirm={() => deactivateDevice.mutate(row.id)}
						>
							<Button type="text" danger loading={deactivateDevice.isPending}>
								{t("devicesTab.deactivate")}
							</Button>
						</Popconfirm>
					</PermissionGate>
				) : (
					<PermissionGate resource="devices" action="update">
						<Popconfirm
							title={t("devicesTab.activateConfirmTitle")}
							description={t("devicesTab.activateConfirmDescription")}
							okText={t("devicesTab.activateConfirmOk")}
							cancelText={t("devicesTab.confirmCancel")}
							onConfirm={() => activateDevice.mutate(row.id)}
						>
							<Button type="text" loading={activateDevice.isPending}>
								{t("devicesTab.activate")}
							</Button>
						</Popconfirm>
					</PermissionGate>
				),
		},
	];

	return (
		<EmployeeListTab<Device>
			employeeId={employeeId}
			queryKey={["employees", "tab", "devices"]}
			url="/devices"
			columns={columns}
		/>
	);
}
